import React from 'react';
import { formatDuration } from '../../utils/formatTime';
import type { SessionEntry, PlayedGameEntry } from '../../types';

interface SessionRowProps {
  entry: SessionEntry & { id: string };
  onDelete?: () => void;
}

export function SessionRow({ entry, onDelete }: SessionRowProps) {
  if (!entry) return null;

  const timestamp = entry.endedAt || entry.startedAt || 0;
  const date = timestamp ? new Date(timestamp) : null;
  const dateStr = date && !isNaN(date.getTime())
    ? date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

  const games: PlayedGameEntry[] = Array.isArray(entry.playedGames)
    ? entry.playedGames
    : entry.playedGames
    ? Object.values(entry.playedGames as Record<string, PlayedGameEntry>)
    : [];

  return (
    <div className="py-2.5 px-3 bg-namm-panel-soft/50 border border-namm-line/20 group hover:border-namm-line/40 transition-colors">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-[10px] text-namm-cyan shrink-0">
            {entry.type === 'stopwatch' ? '⏱' : '⏳'}
          </span>
          <div className="min-w-0">
            <span className="text-xs font-mono text-namm-text truncate block font-semibold">
              {entry.timerName || 'Chronomètre'}
            </span>
            <span className="text-[10px] font-mono text-namm-muted">
              {dateStr} · par {entry.stoppedBy || 'Anonyme'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs font-mono text-namm-cyan tabular-nums font-semibold">
            {formatDuration(entry.totalDurationMs || 0)}
          </span>
          {onDelete && (
            <button
              onClick={onDelete}
              className="text-namm-muted hover:text-namm-danger transition-colors opacity-0 group-hover:opacity-100 text-xs"
              title="Supprimer cette session"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Played Games Timeline Badges */}
      {games.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-namm-line/15">
          {games.map((pg, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 text-[9px] font-mono bg-namm-cyan/10 border border-namm-cyan/30 text-namm-cyan px-1.5 py-0.5"
            >
              <span>🎮</span>
              <span className="font-semibold">{pg.game}</span>
              {pg.durationMs && (
                <span className="text-namm-muted font-normal">
                  ({formatDuration(pg.durationMs)})
                </span>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
